import { ccfRoutes } from '@app/ccf/ccf-routes';
import { useCCF } from '@app/ccf/company-report/ccf-context';
import CCFHubPreviewSheet from '@app/ccf/company-report/pages/ccf-hub-page-preview-sheet';
import useAuth from '@app/auth/use-auth';
import useUpsellDialog from '@app/hooks/use-upsell-dialog';
import { Icons } from '@shared/components/content/icons';
import Text from '@shared/components/content/text';
import { SimpleTooltip } from '@shared/components/simple-tooltip';
import { Button } from '@shared/components/ui/button';
import { cn } from '@shared/lib/utils';
import { formatDate } from 'date-fns';
import { generatePath, Link, useNavigate } from 'react-router-dom';

export default function CCFHubSidebar() {
  const {
    uuid: reportUuid,
    status: reportStatus,
    lastPublishedAt,
    firstPublishedAt,
  } = useCCF();
  const { user } = useAuth();
  const navigate = useNavigate();
  const hasSecr = !!user?.features?.secr;
  const { showDialog: showSecrUpsell, UpsellDialogComponent } =
    useUpsellDialog();

  return (
    <div className="hidden h-min w-full max-w-[400px] space-y-4 md:block">
      <div className="space-y-4 border-b pb-4">
        <div className="flex  flex-wrap-reverse items-center justify-end gap-4">
          {firstPublishedAt && lastPublishedAt && (
            <div className="flex justify-end">
              <Link
                to={generatePath(ccfRoutes.REPORT_RESULT, {
                  report_uuid: reportUuid,
                })}
              >
                <Button variant="outline">View report</Button>
              </Link>
            </div>
          )}
          <div className="flex flex-1 justify-end space-x-4 text-right">
            <div>
              <Text className="text-sm font-medium">First published</Text>
              <Text
                variant="subtle"
                className={cn({
                  'text-gray-300': !firstPublishedAt,
                })}
              >
                {firstPublishedAt
                  ? formatDate(firstPublishedAt, 'do MMM yyyy')
                  : '–'}
              </Text>
            </div>
            <div>
              <Text className="text-sm font-medium">Last published</Text>
              <Text
                variant="subtle"
                className={cn({
                  'text-gray-300': !lastPublishedAt,
                })}
              >
                {lastPublishedAt
                  ? formatDate(lastPublishedAt, 'do MMM yyyy')
                  : '–'}
              </Text>
            </div>
          </div>
        </div>

        {reportStatus === 'edited' && (
          <div className="flex items-center gap-2 text-gray-500">
            <Icons.info className="size-4 shrink-0" />
            <Text variant="subtle" className="text-sm">
              You have unpublished changes not visible in your report
            </Text>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between gap-4 border-b pb-4">
        <Text variant="subtle" className="text-xs">
          Get quick insights across scopes without publishing your report
        </Text>
        <CCFHubPreviewSheet />
      </div>

      {UpsellDialogComponent}
      <div className="space-y-2">
        <div className="flex items-center gap-1 ">
          <Icons.fileChartColumn width={16} height={16} />
          <Text className="text-sm font-medium">
            Streamlined energy & carbon reporting
          </Text>
        </div>
        <div className="flex items-center justify-between gap-4 ">
          <Text variant="subtle" className="text-xs">
            You can now generate a SECR-compliant report from your company
            carbon report data
          </Text>
          <SimpleTooltip
            disabled={hasSecr}
            trigger={
              <span className="block">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!hasSecr}
                  onClick={() =>
                    navigate(
                      generatePath(ccfRoutes.SECR_CONFIGURATION, {
                        report_uuid: reportUuid,
                      })
                    )
                  }
                >
                  Manage SECR
                </Button>
              </span>
            }
            content={
              <div className="max-w-56 space-y-2 p-1 text-sm">
                <p>SECR reporting is not included in your current plan.</p>
                <button
                  className="font-medium underline underline-offset-2"
                  onClick={showSecrUpsell}
                >
                  Book a call to add it →
                </button>
              </div>
            }
          />
        </div>
      </div>
    </div>
  );
}
